import _extends from "@babel/runtime/helpers/extends";
import axios from 'axios';
import _cloneDeep from 'lodash/cloneDeep';
import _find from 'lodash/find';
import _values from 'lodash/values';
import _get from 'lodash/get';
import jstz from 'jstimezonedetect';
import { isClient } from "../../utils/ssr.utils/index.js";
import { $cookie } from "../cookie.service/index.js";
import { CURRENCY, LANGUAGE } from "../../constants/index.js";
/**
 * Http Service
 */
export var HttpService = /*#__PURE__*/function () {
  /**
   * constructor
   */
  function HttpService() {
    this.httpClient = void 0;
    this.baseCommonHeaders = void 0;
    this.pendingGroups = {};
    this.pendingRequests = {};
    this.formatEndpoint = function (endpoint) {
      return endpoint;
    };
    /**
     * get
     * @description an http 'GET' request method function signature
     */
    this.get = this.makeRequest('get');
    /**
     * get
     * @description an http 'POST' request method function signature
     */
    this.post = this.makeRequest('post');
    /**
     * get
     * @description an http 'PUT' request method function signature
     */
    this.put = this.makeRequest('put');
    /**
     * get
     * @description an http 'PATCH' request method function signature
     */
    this.patch = this.makeRequest('patch');
    /**
     * get
     * @description an http 'DELETE' request method function signature
     */
    this["delete"] = this.makeRequest('delete');
    /**
     * Performing concurrent requests
     * @param {Array} requests concurrent requests
     * @return {Promise}
     */
    this.all = axios.all;
    var axiosInstance = axios.create();
    var common = axiosInstance.defaults.headers.common;
    delete common['X-Requested-With'];
    var baseCommonHeaders = _cloneDeep(common);
    axiosInstance.defaults.headers.common = _extends({}, common, {
      Accept: 'application/json, text/javascript',
      'Content-Type': 'application/json; charset=UTF-8',
      'x-locale': $cookie.get('language') || LANGUAGE.EN,
      'x-currency': $cookie.get('currency') || CURRENCY.BASE_SYMBOL,
      'x-tz': jstz.determine().name()
    });
    axiosInstance.interceptors.response.use(function (response) {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    }, function (error) {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      try {
        var data = {
          uri: _get(error, 'config.url', ''),
          method: _get(error, 'config.method', ''),
          requestHeaders: _get(error, 'config.headers', ''),
          body: _get(error, 'config.data', ''),
          responseData: undefined,
          responseStatus: undefined,
          responseHeaders: undefined,
          path: undefined,
          host: undefined,
          noResponse: undefined,
          requestSettingUpFailure: undefined
        };
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          data.responseData = _get(error, 'response.data', '');
          data.responseStatus = _get(error, 'response.status', '');
          data.responseHeaders = _get(error, 'response.headers', '');
          data.path = _get(error.response, 'request.path');
          data.host = _get(error.response, 'request.host');
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          data.noResponse = true;
        } else {
          // Something happened in setting up the request that triggered an Error
          data.requestSettingUpFailure = error.message;
        }
        axios.post('https://www.almosafer.com/datadog/log', data).then(function (response) {})["catch"](function (e) {});
      } catch (e) {}
      return Promise.reject(error);
    });
    this.httpClient = axiosInstance;
    this.baseCommonHeaders = baseCommonHeaders;
  }

  /**
   * cancel pending requests
   */
  var _proto = HttpService.prototype;
  _proto.cancelPendingRequest = function cancelPendingRequest(requestId) {
    if (requestId in this.pendingRequests) {
      this.pendingRequests[requestId]();
      delete this.pendingRequests[requestId];
    }
  }

  /**
   * cancel all pending requests
   */;
  _proto.cancelAllPendingRequests = function cancelAllPendingRequests() {
    var _this = this;
    Object.keys(this.pendingRequests).forEach(function (requestId) {
      _this.cancelPendingRequest(requestId);
    });
  }

  /**
   * get request payload
   */;
  _proto.getRequestPayload = function getRequestPayload(method, endpoint, config) {
    if (config === void 0) {
      config = {};
    }
    return _extends({
      url: this.formatEndpoint(endpoint),
      method: method
    }, config);
  }

  /**
   * format base url
   */;
  _proto.formatBaseUrl = function formatBaseUrl(baseURL) {
    this.httpClient.defaults.baseURL = baseURL;
  }

  /**
   * add headers
   */;
  _proto.addHeaders = function addHeaders(customHeaders) {
    this.httpClient.defaults.headers.common = _extends({}, this.httpClient.defaults.headers.common, customHeaders);
  }

  /**
   * Override headers
   * @param customHeaders
   */;
  _proto.overrideHeaders = function overrideHeaders(customHeaders) {
    this.httpClient.defaults.headers.common = _extends({}, this.baseCommonHeaders, customHeaders);
  }

  /**
   * remove headers
   * @param {Array<string>} headersKeys
   */;
  _proto.removeHeaders = function removeHeaders(headersKeys) {
    var _this2 = this;
    headersKeys.forEach(function (key) {
      delete _this2.httpClient.defaults.headers.common[key];
    });
  }

  /**
   * add endpoint formatter
   * @param {function} handler [takes `url` and returns `url`]
   */;
  _proto.addEndpointFormatter = function addEndpointFormatter(_temp) {
    var _ref = _temp === void 0 ? {} : _temp,
      handler = _ref.handler;
    if (handler) {
      this.formatEndpoint = handler;
    }
  }

  /**
   * add interceptor
   * @param {function} request [takes `requestConfig` and returns `requestConfig`]
   * @param {function} requestError [takes an `error` and returns an `error`]
   * @param {function} response [takes `response` and returns `response`]
   * @param {function} responseError [takes an `error` and returns an `error`]
   */;
  _proto.addInterceptor = function addInterceptor(_ref2) {
    var request = _ref2.request,
      requestError = _ref2.requestError,
      response = _ref2.response,
      responseError = _ref2.responseError;
    if (request) {
      this.httpClient.interceptors.request.use(request);
    }
    if (requestError) {
      this.httpClient.interceptors.request.use(function (req) {
        return req;
      }, function (error) {
        var err = requestError(error);
        return Promise.reject(err);
      });
    }
    if (response) {
      this.httpClient.interceptors.response.use(response);
    }
    if (responseError) {
      this.httpClient.interceptors.response.use(function (res) {
        return res;
      }, function (error) {
        var err = responseError(error);
        return Promise.reject(err);
      });
    }
  }

  /**
   * is cancellation error
   * @param {Object} error [axios error]
   * @return {boolean}
   */;
  _proto.isCancellationError = function isCancellationError(error) {
    return error.toString() === 'Cancel';
  }

  /**
   * handle requests group cancellation
   *
   * @description
   * this method handles a group of requests cancellation
   * if a new a group of requests made with the same group name
   * it can be used for polling cases
   *
   * ex: $http.get(requestId, endpoint, { groupName: 'ASYNC_SEARCH', groupId: sId }).then(({ data }) =>  data);
   *
   * @param {String} groupName [fixed for the new requests and old requests]
   * @param {String} groupId [unique between old requests and new requests]
   * @param {Function} canceller [axios cancel handler]
   * @param {Object} pendingGroups [this.pendingGroups]
   * @return {Object} new pendingGroups
   */;
  _proto.handleRequestsGroupCancellation = function handleRequestsGroupCancellation(_ref3) {
    var _newPendingGroups$gro, _newPendingGroups$gro2, _newPendingGroups$gro3, _newPendingGroups$gro4, _newPendingGroups$gro5, _newPendingGroups$gro6;
    var groupName = _ref3.groupName,
      groupId = _ref3.groupId,
      canceller = _ref3.canceller,
      pendingGroups = _ref3.pendingGroups;
    if (!groupName || !groupId) return pendingGroups;
    var newPendingGroups = _extends({}, pendingGroups);
    var isGroupExist = (groupName in newPendingGroups);
    var isNewGroupId = !_find(_values(newPendingGroups[groupName]), {
      id: groupId
    });
    var isActiveGroupId = isGroupExist && groupId === ((_newPendingGroups$gro = newPendingGroups[groupName].active) === null || _newPendingGroups$gro === void 0 ? void 0 : _newPendingGroups$gro.id);
    var isOldGroupId = isGroupExist && groupId === ((_newPendingGroups$gro2 = newPendingGroups[groupName].old) === null || _newPendingGroups$gro2 === void 0 ? void 0 : _newPendingGroups$gro2.id);
    switch (true) {
      case !isGroupExist:
        newPendingGroups[groupName] = {
          active: {
            id: groupId,
            requests: [canceller]
          },
          old: {
            id: null,
            requests: []
          }
        };
        break;
      case isActiveGroupId:
        (_newPendingGroups$gro3 = newPendingGroups[groupName].active) === null || _newPendingGroups$gro3 === void 0 ? void 0 : (_newPendingGroups$gro4 = _newPendingGroups$gro3.requests) === null || _newPendingGroups$gro4 === void 0 ? void 0 : _newPendingGroups$gro4.push(canceller);
        break;
      case isOldGroupId:
        canceller();
        break;
      case isNewGroupId:
        newPendingGroups[groupName] = {
          active: {
            id: groupId,
            requests: [canceller]
          },
          old: _extends({}, newPendingGroups[groupName].active)
        };
        (_newPendingGroups$gro5 = newPendingGroups[groupName].old) === null || _newPendingGroups$gro5 === void 0 ? void 0 : (_newPendingGroups$gro6 = _newPendingGroups$gro5.requests) === null || _newPendingGroups$gro6 === void 0 ? void 0 : _newPendingGroups$gro6.forEach(function (canceller) {
          return canceller();
        });
        break;
    }
    return newPendingGroups;
  }

  /**
   * request method
   * @description an http request method function signature
   * @typedef {(requestId: string, endpoint: string, config ?: object) => Promise} RequestMethod
   */

  /**
   * make request method
   * @param {string} method
   * @return {RequestMethod}
   */;
  _proto.makeRequest = function makeRequest(method) {
    var _this3 = this;
    return function (requestId, endpoint, config) {
      if (isClient()) {
        _this3.cancelPendingRequest(requestId);
      }
      var requestPayload = _this3.getRequestPayload(method, endpoint, config);
      if (isClient()) {
        requestPayload.cancelToken = new axios.CancelToken(function (canceller) {
          _this3.pendingRequests[requestId] = canceller;
          if (typeof requestPayload.groupId === 'string' && typeof requestPayload.groupName === 'string') {
            _this3.pendingGroups = _this3.handleRequestsGroupCancellation({
              groupId: requestPayload.groupId,
              groupName: requestPayload.groupName,
              pendingGroups: _this3.pendingGroups,
              canceller: canceller
            });
          }
        });
      }
      return _this3.httpClient.request(requestPayload);
    };
  };
  return HttpService;
}();
export var $http = new HttpService();